/**
 * Conversion entre une policy IAM AWS (format JSON "officiel") et le format
 * attendu par la data source Terraform `aws_iam_policy_document` (tableau de
 * `statement`, clés en minuscules / pluriel, `condition` sous forme de liste).
 *
 * Les deux sens sont supportés :
 *  - policyToTerraform : { Version, Statement: [...] }  ->  [{ sid, effect, actions, ... }]
 *  - terraformToPolicy : [{ sid, effect, actions, ... }]  ->  { Version, Statement: [...] }
 */

type StringOrArray = string | string[];

export interface IamPrincipal {
  [type: string]: StringOrArray;
}

export interface IamStatement {
  Sid?: string;
  Effect: string;
  Action?: StringOrArray;
  NotAction?: StringOrArray;
  Resource?: StringOrArray;
  NotResource?: StringOrArray;
  Principal?: '*' | IamPrincipal;
  NotPrincipal?: '*' | IamPrincipal;
  Condition?: Record<string, Record<string, StringOrArray>>;
}

export interface IamPolicy {
  Version?: string;
  Id?: string;
  Statement: IamStatement | IamStatement[];
}

export interface TerraformCondition {
  test: string;
  variable: string;
  values: string[];
}

export interface TerraformPrincipal {
  type: string;
  identifiers: string[];
}

export interface TerraformStatement {
  sid?: string;
  effect?: string;
  actions?: string[];
  not_actions?: string[];
  resources?: string[];
  not_resources?: string[];
  principals?: TerraformPrincipal[];
  not_principals?: TerraformPrincipal[];
  condition?: TerraformCondition[];
}

const toArray = (value: StringOrArray | undefined): string[] | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  return Array.isArray(value) ? value : [value];
};

/** Réduit un tableau à une valeur scalaire s'il ne contient qu'un élément. */
const collapse = (values: string[]): StringOrArray =>
  values.length === 1 ? values[0] : values;

const capitalizeEffect = (effect?: string): string => {
  if (!effect) {
    return 'Allow';
  }
  const lower = effect.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const principalToTerraform = (
  principal: '*' | IamPrincipal,
): TerraformPrincipal[] => {
  if (principal === '*') {
    return [{ type: '*', identifiers: ['*'] }];
  }
  return Object.entries(principal).map(([type, identifiers]) => ({
    type,
    identifiers: toArray(identifiers) ?? [],
  }));
};

const principalToIam = (
  principals: TerraformPrincipal[],
): '*' | IamPrincipal => {
  if (
    principals.length === 1 &&
    principals[0].type === '*' &&
    principals[0].identifiers.length === 1 &&
    principals[0].identifiers[0] === '*'
  ) {
    return '*';
  }
  const result: IamPrincipal = {};
  for (const { type, identifiers } of principals) {
    result[type] = collapse(identifiers);
  }
  return result;
};

/** Convertit une policy IAM vers le tableau de statements Terraform. */
export const policyToTerraform = (policy: IamPolicy): TerraformStatement[] => {
  if (!policy || typeof policy !== 'object' || !('Statement' in policy)) {
    throw new Error(
      "JSON invalide : une policy IAM doit contenir une clé 'Statement'.",
    );
  }

  const statements = Array.isArray(policy.Statement)
    ? policy.Statement
    : [policy.Statement];

  return statements.map(stmt => {
    const out: TerraformStatement = {};

    if (stmt.Sid !== undefined) {
      out.sid = stmt.Sid;
    }
    out.effect = capitalizeEffect(stmt.Effect);

    const actions = toArray(stmt.Action);
    if (actions) {
      out.actions = actions;
    }
    const notActions = toArray(stmt.NotAction);
    if (notActions) {
      out.not_actions = notActions;
    }
    const resources = toArray(stmt.Resource);
    if (resources) {
      out.resources = resources;
    }
    const notResources = toArray(stmt.NotResource);
    if (notResources) {
      out.not_resources = notResources;
    }
    if (stmt.Principal !== undefined) {
      out.principals = principalToTerraform(stmt.Principal);
    }
    if (stmt.NotPrincipal !== undefined) {
      out.not_principals = principalToTerraform(stmt.NotPrincipal);
    }

    if (stmt.Condition) {
      const conditions: TerraformCondition[] = [];
      for (const [test, variables] of Object.entries(stmt.Condition)) {
        for (const [variable, value] of Object.entries(variables)) {
          conditions.push({
            test,
            variable,
            values: toArray(value) ?? [],
          });
        }
      }
      if (conditions.length > 0) {
        out.condition = conditions;
      }
    }

    return out;
  });
};

/** Réordonne les clés d'un statement IAM pour un rendu lisible et canonique. */
const orderIamStatement = (stmt: IamStatement): IamStatement => {
  const ordered: IamStatement = {} as IamStatement;
  const order: (keyof IamStatement)[] = [
    'Sid',
    'Effect',
    'Principal',
    'NotPrincipal',
    'Action',
    'NotAction',
    'Resource',
    'NotResource',
    'Condition',
  ];
  for (const key of order) {
    if (stmt[key] !== undefined) {
      (ordered as unknown as Record<string, unknown>)[key] = stmt[key];
    }
  }
  return ordered;
};

/** Convertit un tableau de statements Terraform vers une policy IAM. */
export const terraformToPolicy = (
  statements: TerraformStatement[],
  version = '2012-10-17',
): IamPolicy => {
  if (!Array.isArray(statements)) {
    throw new Error(
      'JSON invalide : le format Terraform attendu est un tableau de statements.',
    );
  }

  const iamStatements: IamStatement[] = statements.map(stmt => {
    const out: IamStatement = {
      Effect: capitalizeEffect(stmt.effect),
    };

    if (stmt.sid !== undefined) {
      out.Sid = stmt.sid;
    }
    if (stmt.actions) {
      out.Action = collapse(stmt.actions);
    }
    if (stmt.not_actions) {
      out.NotAction = collapse(stmt.not_actions);
    }
    if (stmt.resources) {
      out.Resource = collapse(stmt.resources);
    }
    if (stmt.not_resources) {
      out.NotResource = collapse(stmt.not_resources);
    }
    if (stmt.principals) {
      out.Principal = principalToIam(stmt.principals);
    }
    if (stmt.not_principals) {
      out.NotPrincipal = principalToIam(stmt.not_principals);
    }

    if (stmt.condition && stmt.condition.length > 0) {
      const condition: Record<string, Record<string, StringOrArray>> = {};
      for (const { test, variable, values } of stmt.condition) {
        if (!condition[test]) {
          condition[test] = {};
        }
        condition[test][variable] = collapse(values ?? []);
      }
      out.Condition = condition;
    }

    return orderIamStatement(out);
  });

  return {
    Version: version,
    Statement: iamStatements,
  };
};

export type ConversionDirection = 'policyToTerraform' | 'terraformToPolicy';

/**
 * Convertit une entrée JSON (chaîne) selon la direction demandée.
 * Retourne la chaîne JSON formatée (2 espaces).
 */
export const convert = (
  input: string,
  direction: ConversionDirection,
  indent = 2,
): string => {
  const trimmed = input.trim();
  if (!trimmed) {
    return '';
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (e) {
    throw new Error(
      `JSON invalide : ${e instanceof Error ? e.message : String(e)}`,
    );
  }

  if (direction === 'policyToTerraform') {
    const result = policyToTerraform(parsed as IamPolicy);
    return JSON.stringify(result, null, indent);
  }

  const result = terraformToPolicy(parsed as TerraformStatement[]);
  return JSON.stringify(result, null, indent);
};
